import { Injectable, inject } from "@angular/core";
import {
  Observable,
  catchError,
  forkJoin,
  from,
  map,
  of,
  switchMap,
  take,
  tap,
} from "rxjs";
import {
  getStorage,
  ref,
  listAll,
  getDownloadURL,
  deleteObject,
  uploadBytes,
} from "firebase/storage";
import { initializeApp } from "firebase/app";
import { environment } from "../../../environments/environment";
import {
  IAcuerdoSeleccionado,
  IArchivoAcuerdo,
  IResponseArchivos,
} from "../../interfaces/acuerdos";
import { Store } from "@ngrx/store";
import { AppState } from "../../app.state";
import { HttpClient } from "@angular/common/http";
import {
  IArchivoAdjunto,
  IBitacora,
  IReqDownloadAllFiles,
} from "../../interfaces/storage";

@Injectable({
  providedIn: "root",
})
export class StorageService {
  private storage = getStorage(initializeApp(environment.firebase));
  private readonly store: Store<AppState> = inject(Store);
  acuerdoSeleccionado$!: Observable<IAcuerdoSeleccionado>;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {}
  obtenerArchivos(_idAcuerdo: string): Observable<IResponseArchivos> {
    const refA = ref(this.storage, `acuerdo/${_idAcuerdo}/`);
    return from(listAll(refA)).pipe(
      switchMap((listResult) => {
        if (listResult.items.length === 0) {
          return of({ archivos: [], error: false });
        }
        return forkJoin(
          listResult.items.map((item, index) =>
            from(getDownloadURL(item)).pipe(
              map(
                (url) =>
                  ({
                    name: item.name,
                    url,
                    nuevo: false,
                    fileId: Number(Date.now()) + index,
                  } as unknown as IArchivoAcuerdo)
              )
            )
          )
        ).pipe(
          map((archivos) => ({
            archivos,
            error: false,
          }))
        );
      }),
      catchError((error) => {
        console.error("Error en obtenerArchivos:", error);
        return of({ error: true, archivos: [] });
      })
    );
  }

  eliminarArchivo(
    idAcuerdo: string,
    nombreArchivo: string
  ): Observable<boolean> {
    const refA = ref(this.storage, `acuerdo/${idAcuerdo}/${nombreArchivo}`);
    return from(deleteObject(refA)).pipe(
      map(() => true),
      catchError((error) => {
        console.error("Error en eliminarArchivo:", error);
        return of(false);
      })
    );
  }

  // Nuevo método para guardar archivos
  guardarArchivos(idAcuerdo: string, archivos: any[]): Observable<any[]> {
    const uploadObservables = archivos.map((file) => {
      const refA = ref(this.storage, `acuerdo/${idAcuerdo}/${file.name}`);
      return from(uploadBytes(refA, file.archivo)).pipe(
        map(() => true),
        catchError((error) => {
          console.error("Error en guardarArchivo:", error);
          return of(false);
        })
      );
    });
    return from(Promise.all(uploadObservables)).pipe(
      map((result) => result),
      catchError((error) => {
        console.error("Error al guardar archivos:", error);
        return of([]);
      })
    );
  }

  dowloadFiles(payload: IReqDownloadAllFiles) {
    return this.http
      .post<any>(`${environment.acuerdos.descargaArchivosAcuerdo}`, { ...payload })
      .pipe(
        tap((data) => console.log("API Response", data)),
        take(1),
        map((data: any) => {
          return {
            error: false,
            data: data,
          };
        }),
        catchError((error) => {
          console.error("Error en dowloadFiles", error);
          return of({ error: true, data: null });
        })
      );
  }

  dowloadBitacora(payload: IBitacora) {
    return this.http
      .post(
        `${environment.acuerdos.exportBitacoraObservaciones}`,
        {
          ...payload,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "arraybuffer",
        }
      )
      .pipe(
        tap((data) => console.log("API Response", data)),
        take(1),
        map((data: any) => {
          return {
            error: false,
            data: data,
          };
        }),
        catchError((error) => {
          console.error("Error en dowloadFiles", error);
          return of({ error: true, data: null });
        })
      );
  }

  eliminarArchivoAdjunto(payload: IArchivoAdjunto) {
    return this.http
      .post<any>(`${environment.acuerdos.eliminarArchivoAdjunto}`, {
        ...payload,
      })
      .pipe(
        tap((data) => console.log("API Response", data)),
        take(1),
        map((data: any) => {
          return {
            error: false,
            data: data,
          };
        }),
        catchError((error) => {
          console.error("Error en dowloadFiles", error);
          return of({ error: true, data: null });
        })
      );
  }

  obtenerArchivoUrl(entradaId: string, nombreArchivo: string): Observable<any> {
    const refA = ref(this.storage, `entradas/${entradaId}/${nombreArchivo}`);

    return from(getDownloadURL(refA)).pipe(
      map((url) => ({
        nombreArchivo,
        url,
        nuevo: false,
        fileId: Date.now(),
        error: false,
      })),
      catchError((error) => {
        console.error("Error en obtenerArchivoUrl:", error);
        return of({ nombreArchivo, error: true });
      })
    );
  }
}
